import DOMPurify from 'dompurify';
import { useMemo, useState } from 'react';
import {
    REPORT_IMPORT_SECTION_IDS,
    reportImportsApi,
    type ReportImportParseResult,
    type ReportImportSectionId,
} from '@/api/report-imports';
import { DOCX_MIME_TYPE, uploadsApi } from '@/api/uploads';
import { Danger, InfoCircle, Spinner } from '@/components/icons';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileDropzone } from '@/components/ui/file-dropzone';
import { Progress } from '@/components/ui/progress';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { extractApiErrorMessage } from '@/lib/api-errors';
import {
    buildReportPatch,
    defaultSectionChoiceFor,
    reportImportPatchHasContent,
    type ReportImportPatch,
    type ReportImportSectionChoice,
} from '@/lib/report-imports';

const REPORT_IMPORT_SECTION_LABELS: Record<ReportImportSectionId, string> = {
    demand_description: 'Descrição da Demanda',
    procedures: 'Procedimentos',
    behavior_observations: 'Observação do Comportamento',
    clinical_history: 'História Clínica',
    tests: 'Análise',
    diagnosis: 'Diagnóstico e Conclusão',
    recommendations: 'Recomendações',
};

const UPLOAD_ERROR_FALLBACK = 'Não foi possível importar o documento. Tente novamente.';

type ReportImportModalProps = {
    hasContent: boolean;
    patientId?: string;
    onApply: (patch: ReportImportPatch) => void;
    onClose: () => void;
};

export const ReportImportModal = ({
    hasContent,
    patientId,
    onApply,
    onClose,
}: ReportImportModalProps) => {
    const [status, setStatus] = useState<'idle' | 'uploading' | 'parsing'>('idle');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [parseResult, setParseResult] = useState<ReportImportParseResult | null>(null);
    const [choices, setChoices] = useState<Record<number, ReportImportSectionChoice>>({});

    const handleFilesSelected = async (files: File[]) => {
        const file = files[0];
        if (!file || status !== 'idle') return;

        if (!file.name.toLowerCase().endsWith('.docx')) {
            setError('Envie um documento Word no formato .docx.');
            return;
        }

        setError(null);
        setUploadProgress(0);
        setStatus('uploading');
        try {
            // The staging key goes to the parse endpoint, which validates and
            // consumes the object itself — never to /uploads/confirm.
            const key = await uploadsApi.uploadToStaging({
                file,
                intent: 'report_import',
                onProgress: setUploadProgress,
            });

            setStatus('parsing');
            const result = await reportImportsApi.parse(key, patientId);

            setParseResult(result);
            setChoices(
                Object.fromEntries(
                    result.segments.map((segment) => [
                        segment.index,
                        defaultSectionChoiceFor(segment),
                    ]),
                ),
            );
        } catch (uploadError) {
            setError(extractApiErrorMessage(uploadError, UPLOAD_ERROR_FALLBACK));
        } finally {
            setStatus('idle');
        }
    };

    const importCount = useMemo(() => {
        if (!parseResult) return 0;
        return parseResult.segments.filter(
            (segment) => (choices[segment.index] ?? defaultSectionChoiceFor(segment)) !== 'skip',
        ).length;
    }, [choices, parseResult]);

    const hasTables = useMemo(
        () => parseResult?.segments.some((segment) => segment.html.includes('<table')) ?? false,
        [parseResult],
    );

    const sanitizedHtmlByIndex = useMemo(
        () =>
            new Map(
                parseResult?.segments.map((segment) => [
                    segment.index,
                    DOMPurify.sanitize(segment.html),
                ]) ?? [],
            ),
        [parseResult],
    );

    const handleConfirm = () => {
        if (!parseResult) return;
        const patch = buildReportPatch(parseResult.segments, choices);
        if (!reportImportPatchHasContent(patch)) {
            setError('Nenhuma seção selecionada para importar.');
            return;
        }
        onApply(patch);
    };

    if (!parseResult) {
        return (
            <div className="space-y-4">
                {status === 'parsing' ? (
                    <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-card p-8 text-center">
                        <Spinner className="size-6 animate-spin text-muted-foreground" />
                        <p className="text-sm font-medium text-foreground">
                            Analisando o documento…
                        </p>
                        <p className="text-xs text-muted-foreground">
                            Identificando as seções do seu laudo.
                        </p>
                    </div>
                ) : (
                    <FileDropzone
                        variant="card"
                        title="Enviar laudo em Word"
                        description="Um arquivo .docx de até 10 MB."
                        selectLabel="Escolher arquivo"
                        accept={`${DOCX_MIME_TYPE},.docx`}
                        loading={status === 'uploading'}
                        showSelectButton={false}
                        onFilesSelected={(files) => void handleFilesSelected(files)}
                    />
                )}

                {status === 'uploading' && <Progress value={uploadProgress} />}

                {error && (
                    <Alert variant="destructive" icon={<Danger className="size-4" />}>
                        {error}
                    </Alert>
                )}
            </div>
        );
    }

    return (
        <div className="flex min-h-0 flex-col gap-4">
            <div className="space-y-2">
                <Alert variant="info" icon={<InfoCircle className="size-4" />}>
                    O texto é importado na íntegra, mas a formatação pode diferir do documento
                    original. Imagens não são importadas.
                </Alert>
                {hasTables && (
                    <Alert variant="info" icon={<InfoCircle className="size-4" />}>
                        Tabelas do documento serão importadas como tabelas na seção Resultados dos
                        Testes.
                    </Alert>
                )}
                {parseResult.warnings.map((warning) => (
                    <Alert key={warning} variant="warning" icon={<Danger className="size-4" />}>
                        {warning}
                    </Alert>
                ))}
                {hasContent && (
                    <Alert variant="warning" icon={<Danger className="size-4" />}>
                        As seções selecionadas terão o conteúdo atual substituído pelo texto
                        importado.
                    </Alert>
                )}
            </div>

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
                {parseResult.segments.map((segment) => {
                    const choice = choices[segment.index] ?? defaultSectionChoiceFor(segment);
                    const isUnidentified = segment.suggested_section === 'unknown';
                    return (
                        <div
                            key={segment.index}
                            className="space-y-2 rounded-2xl border border-border bg-card p-3 shadow-sm"
                        >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="flex min-w-0 items-center gap-2">
                                    <p className="truncate text-sm font-medium text-foreground">
                                        {segment.display_title ??
                                            segment.title ??
                                            'Texto inicial do documento'}
                                    </p>
                                    {isUnidentified && choice === 'skip' && (
                                        <Badge variant="neutral">Não será importada</Badge>
                                    )}
                                    {!isUnidentified &&
                                        segment.confidence === 'low' &&
                                        choice !== 'skip' && (
                                            <Badge variant="warning">Conferir seção</Badge>
                                        )}
                                </div>
                                <Select
                                    value={choice}
                                    onValueChange={(value: string) =>
                                        setChoices((current) => ({
                                            ...current,
                                            [segment.index]: value as ReportImportSectionChoice,
                                        }))
                                    }
                                >
                                    <SelectTrigger className="w-full sm:w-64">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {REPORT_IMPORT_SECTION_IDS.map((sectionId) => (
                                            <SelectItem key={sectionId} value={sectionId}>
                                                {REPORT_IMPORT_SECTION_LABELS[sectionId]}
                                            </SelectItem>
                                        ))}
                                        <SelectItem value="skip">Não importar</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {isUnidentified && !segment.empty_body && (
                                <p className="text-xs text-warning">
                                    Não identificamos a qual seção este trecho pertence. Escolha uma
                                    seção para importá-lo.
                                </p>
                            )}
                            {segment.empty_body ? (
                                <p className="text-xs italic text-muted-foreground">
                                    Apenas título, sem conteúdo.
                                </p>
                            ) : (
                                <div
                                    className="max-h-28 overflow-y-auto rounded-lg bg-surface-muted p-2 text-xs leading-relaxed text-muted-foreground [&_table]:w-full [&_td]:border [&_td]:border-border [&_td]:p-1"
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizedHtmlByIndex.get(segment.index) ?? '',
                                    }}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            {error && (
                <Alert variant="destructive" icon={<Danger className="size-4" />}>
                    {error}
                </Alert>
            )}

            <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
                <Button variant="outline" onClick={onClose}>
                    Cancelar
                </Button>
                <Button onClick={handleConfirm} disabled={importCount === 0}>
                    {hasContent ? 'Substituir e importar' : 'Importar'}
                </Button>
            </div>
        </div>
    );
};
